import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Loader2, Plug } from 'lucide-react';
import { connectorsApi } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Intégrations — un panneau, un bouton.
 *
 * Le client : « tu fais un sous-menu intégration, et tu affiches juste un bouton
 * "request connecteurs" qui trigger l'envoi d'un mail vers admin@aikka.ai ».
 *
 * CE QU'IL NE FAIT PAS, ET C'EST TOUT L'INTÉRÊT. Il n'y a pas de catalogue, pas
 * d'état « bientôt disponible » par fournisseur, pas de bascule qui ne bascule
 * rien : aucun connecteur n'est provisionné, et une grille de logos grisés se lit
 * comme une fonctionnalité cassée plutôt qu'absente. Le panneau dit ce qui est
 * vrai — il n'y en a pas encore — et propose la seule action qui existe
 * réellement : prévenir l'équipe.
 *
 * DEUX ÉTATS APRÈS LE CLIC, et ils ne disent pas la même chose. « Envoyée »
 * signifie qu'un courriel est parti. « Déjà envoyée » signifie que la route a
 * refusé d'en envoyer un second dans les 24 h — l'étranglement qui protège la
 * boîte de l'équipe — et l'opérateur mérite de savoir que sa demande est en
 * main, pas qu'elle a échoué.
 */
export function SettingsIntegrationsSection() {
  const { t } = useTranslation('dashboard');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'already' | 'error'>('idle');

  const request = async () => {
    setState('sending');
    try {
      const { data } = await connectorsApi.request();
      setState(data.alreadyRequested ? 'already' : 'sent');
    } catch {
      setState('error');
    }
  };

  return (
    <div className="space-y-4" data-testid="settings-integrations">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Plug className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <CardTitle className="text-base">{t('settings.integrations.title')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('settings.integrations.description')}
          </p>

          <Button
            size="sm"
            data-testid="settings-integrations-request"
            onClick={request}
            disabled={state === 'sending' || state === 'sent' || state === 'already'}
          >
            {state === 'sending' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              t('settings.integrations.request')
            )}
          </Button>

          {state === 'sent' || state === 'already' ? (
            <p
              role="status"
              data-testid="settings-integrations-sent"
              className="flex items-start gap-1.5 text-xs text-muted-foreground"
            >
              <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t(
                state === 'already'
                  ? 'settings.integrations.alreadySent'
                  : 'settings.integrations.sent',
              )}
            </p>
          ) : null}

          {state === 'error' ? (
            <p
              role="alert"
              data-testid="settings-integrations-error"
              className="flex items-start gap-1.5 text-xs text-destructive"
            >
              <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t('settings.integrations.error')}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
